import { BASE_LINE_HEIGHT_MOD, defaultRootUnitSizePx } from '../constants';
import { removeGameObjectByEntity } from '../entity';
import {
  addV2,
  containsLink,
  isVisibleWordType,
  removeDenseArrayIndexAndKeepOrder,
  replaceInDenseArray,
  v2,
} from '../helpers';
import { Alignment_Horizontal } from '../shared/api_shared_types';
import {
  CommandType,
  CORNER,
  PADDING,
  Shape,
  SIDE,
  TextBlockType,
  WordType,
  type Size,
  type TextBlock,
  type TextLine,
  type TextObject,
  type Word,
} from '../types';
import logger from './logger';
import { createWord } from '../textobject/text_parser';

export function layoutTextObject(tObj: TextObject) {
  //update text objects
  const manager = tObj.manager;

  for (let i = 0; i < tObj.hoverContainers.length; i++) {
    removeGameObjectByEntity(tObj.hoverContainers[i].container);
  }
  tObj.hoverContainers.length = 0;

  tObj.maxWidthPx = tObj.textStyle.doAutoResize
    ? tObj.maxWidthEm * manager.rootUnitSizePx
    : tObj.maxWidthEm * defaultRootUnitSizePx;

  if (tObj.shape === Shape.CIRCLE) {
    let oneLineWidth = tObj.words.reduce((total, t) => total + t.width, 0);
    tObj.maxWidthPx = Math.sqrt(tObj.fontSizePx * oneLineWidth) * 1.3;
  }
  let oldWidth = 0;
  let oldHeight = 0;

  oldWidth = tObj.width;
  oldHeight = tObj.height;

  //mark special words
  for (let i = 0; i < tObj.words.length; i++) {
    const word = tObj.words[i];
    if (containsLink(word.text)) {
      word.command = {
        type: CommandType.ACTION,
        value: 'external_link',
        args: [word.text],
      };
    }
  }

  //check words that are longer than the maxWidth
  for (let i = tObj.words.length - 1; i >= 0; i--) {
    const word = tObj.words[i];
    if (word.width > tObj.maxWidthPx) {
      const splitWords = splitWord(
        word,
        tObj.maxWidthPx,
        tObj.fontSizePx,
        tObj.manager.textRenderCtx,
      );

      replaceInDenseArray(tObj.words, splitWords, i);
    }
  }

  tObj.textEnt = {
    pos: v2(),
    posOffset: v2(),
    width: 0,
    height: 0,
    textBlocks: divideWordsIntoTextBlocks(tObj.words),
    hoverBlock: null,
    hoverLine: null,
    hoverWord: null,
  };

  let lastLineNr = 0;
  let isLastLine = false;

  if (tObj.textEnt.textBlocks.length === 0) {
    const emptyTextBlock = getZeroTextBlock();
    tObj.textEnt.textBlocks.push(emptyTextBlock);
  }

  for (let i = 0; i < tObj.textEnt.textBlocks.length; i++) {
    const block = tObj.textEnt.textBlocks[i];

    [isLastLine, lastLineNr] = createLinesInTextBlock(block, tObj.maxWidthPx, lastLineNr, tObj);

    layoutTextBlock(block, tObj);
    if (isLastLine) {
      break;
    }
  }

  //remove empty lines on the end
  if (tObj.removeEmptyLinesOnEnd) {
    if (tObj.textEnt.textBlocks.length > 1) {
      for (let i = tObj.textEnt.textBlocks.length - 1; i >= 0; i--) {
        if (tObj.textEnt.textBlocks[i].type === TextBlockType.WHITE_SPACE) {
          tObj.textEnt.textBlocks.length--;
        } else {
          break;
        }
      }
    }
  }

  //Mutates array
  if (tObj.removeDoubleWhiteSpaces) {
    removeDoubleWhiteSpaceBlocks(tObj.textEnt.textBlocks);
  }
  removeWhiteSpaceBetweenListItems(tObj.textEnt.textBlocks);

  let blockYPos = 0;
  let textEntWidth = 0;
  let textEntHeight = 0;

  for (let i = 0; i < tObj.textEnt.textBlocks.length; i++) {
    const block = tObj.textEnt.textBlocks[i];
    if (
      i > 0 &&
      block.type === TextBlockType.LIST_ITEM &&
      tObj.textEnt.textBlocks[i - 1].type === TextBlockType.ALINEA
    ) {
      blockYPos += block.lines[0].height * 0.6;
    }
    block.pos.y = blockYPos;
    blockYPos +=
      block.height +
      (BASE_LINE_HEIGHT_MOD * tObj.textStyle.lineHeightMod - 1) *
        tObj.fontSizePx *
        tObj.textStyle.alineaSpacingMod;
    if (textEntWidth < block.width + block.pos.x) {
      textEntWidth = block.width + block.pos.x;
    }

    const minLineHeight = tObj.fontSizePx;
    textEntHeight = Math.max(block.pos.y + block.height, minLineHeight);

    textEntWidth = Math.max(textEntWidth, minLineHeight * tObj.minWidthMod);
  }

  tObj.textEnt.width = textEntWidth;
  tObj.textEnt.height = textEntHeight;

  if (tObj.textStyle.textAlign !== Alignment_Horizontal.LEFT) {
    for (let i = 0; i < tObj.textEnt.textBlocks.length; i++) {
      const block = tObj.textEnt.textBlocks[i];
      const diff = tObj.textEnt.width - block.width;
      if (tObj.textStyle.textAlign === Alignment_Horizontal.CENTER) {
        block.pos.x = diff / 2;
      } else if (tObj.textStyle.textAlign === Alignment_Horizontal.RIGHT) {
        block.pos.x = diff;
      }
    }
  }

  tObj.textEnt.pos = v2(-tObj.textEnt.width / 2, -tObj.textEnt.height / 2);

  if (tObj.shape === Shape.RECT) {
    let m = unpackPadding(tObj.textStyle.padding);

    const fullHeight = tObj.textEnt.height + m[PADDING.TOP] + m[PADDING.BOTTOM];
    let addToLeft = 0;
    let addToRight = 0;

    if (tObj.textStyle.roundedSides[SIDE.LEFT]) {
      tObj.textStyle.cornerRadius[CORNER.TOP_LEFT] = fullHeight;
      tObj.textStyle.cornerRadius[CORNER.BOTTOM_LEFT] = fullHeight;

      addToLeft = fullHeight / 3;
    }
    if (tObj.textStyle.roundedSides[SIDE.RIGHT]) {
      tObj.textStyle.cornerRadius[CORNER.TOP_RIGHT] = fullHeight;
      tObj.textStyle.cornerRadius[CORNER.BOTTOM_RIGHT] = fullHeight;

      addToRight = fullHeight / 3;
    }

    tObj.textStyle.padding = m;

    tObj.width = tObj.textEnt.width + m[PADDING.LEFT] + m[PADDING.RIGHT] + addToLeft + addToRight;
    tObj.height = tObj.textEnt.height + m[PADDING.TOP] + m[PADDING.BOTTOM];
    let paddingDiffLeftRight = m[PADDING.LEFT] + addToLeft - (m[PADDING.RIGHT] + addToRight);
    let paddingDiffTopBottom = m[PADDING.TOP] - m[PADDING.BOTTOM];
    tObj.textEnt.pos.x = -(tObj.textEnt.width / 2) + paddingDiffLeftRight / 2;
    tObj.textEnt.pos.y = -(tObj.textEnt.height / 2) + paddingDiffTopBottom / 2;
  } else {
    let m = unpackPadding(tObj.textStyle.padding);

    let padding = m[PADDING.LEFT]; //make margin?
    let widthSqr = tObj.textEnt.width * tObj.textEnt.width;
    let heightSqr = tObj.textEnt.height * tObj.textEnt.height;
    let squaredSize = Math.sqrt(widthSqr + heightSqr);
    let diameter = Math.max(squaredSize, tObj.minWidthMod);

    let desiredRadius = diameter / 2 + padding;
    tObj.radius = Math.max(desiredRadius); //TODO we once had a minRadius on Entity. Check whats neede.
    tObj.textEnt.pos.x = -tObj.textEnt.width / 2;
    tObj.textEnt.pos.y = -tObj.textEnt.height / 2;
  }
  for (const textBlock of tObj.textEnt.textBlocks) {
    for (const line of textBlock.lines) {
      for (const word of line.words) {
        if (word.type === WordType.Anchor) {
          const size = 0.8 * word.wordStyle.fontSizePx;
          const offsetX = size / 2;
          const offsetY = size / 8 - size / 2;
          const offset = v2(offsetX, word.height / 2 - offsetY);

          tObj.attachpoints.push({
            tag: 'anchor',
            pos: addV2(tObj.textEnt.pos, textBlock.pos, line.pos, word.localPos, offset),
            dir: {
              x: 0,
              y: 1,
            },
          });
        }
      }
    }
  }

  tObj.layoutChanged = true;
}

function splitWord(
  word: Word,
  maxLength: number,
  fontSizePx: number,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
): Word[] {
  const charArray = word.text.split('');

  const splitText: string[] = [''];

  for (let i = 0; i < charArray.length; i++) {
    const textToTry = splitText[splitText.length - 1] + charArray[i];

    const length = getTextWidth(ctx, word.wordStyle.fontId, fontSizePx, textToTry);

    if (length > maxLength) {
      //too long, split text
      splitText.push(charArray[i]);
    } else {
      //ok, add char
      splitText[splitText.length - 1] = textToTry;
    }
  }

  const result: Word[] = splitText.map((it) => {
    const newWord = createWord(
      ctx,
      it.split(''),
      word.type,
      fontSizePx,
      word.textColor,
      true,
      word.wordStyle,
    );

    newWord.command = word.command;
    return newWord;
  });

  return result;
}

function divideWordsIntoTextBlocks(words: Word[]): TextBlock[] {
  let textBlocks: TextBlock[] = [];
  let currentTextBlock = getZeroTextBlock();
  let previousTextBlock = getZeroTextBlock();

  for (let i = 0; i < words.length; i++) {
    currentTextBlock.words.push(words[i]);

    if (words[i].type === WordType.AlineaEnd) {
      if (currentTextBlock.words.length > 2) {
        const visibleWords = currentTextBlock.words.filter((it) => isVisibleWordType(it.type));
        if (visibleWords.length === 0) {
          //this is an empty alinea, remove all words inbetween
          currentTextBlock.words[1] = currentTextBlock.words[currentTextBlock.words.length - 1];
          currentTextBlock.words.length = 2;
        }
      }

      textBlocks.push(currentTextBlock);
      previousTextBlock = currentTextBlock;
      currentTextBlock = getZeroTextBlock();
    }
  }

  return textBlocks;
}

function createLinesInTextBlock(
  block: TextBlock,
  maxWidthPx: number,
  lastLineNr: number,
  tObj: TextObject,
): [boolean, number] {
  if (block.words.length === 2 && block.words[1].type === WordType.AlineaEnd) {
    if (tObj.maxLines === 0 || lastLineNr <= tObj.maxLines) {
      block.type = TextBlockType.WHITE_SPACE;
      block.height = tObj.fontSizePx * 0.5;
      block.lines.push({
        ...getZeroTextLine(),
        words: [...block.words],
        lineNr: ++lastLineNr,
        height: tObj.fontSizePx * 0.5,
      });
      return [false, lastLineNr];
    } else {
      return [true, 0];
    }
  } else {
    block.type = TextBlockType.ALINEA;
    block.maxWidthPx = maxWidthPx;
  }

  let currentLine = getZeroTextLine(++lastLineNr);
  let wordPacketLine = getZeroTextLine();

  for (let i = 0; i < block.words.length; i++) {
    const word = block.words[i];
    const isLastWord = i === block.words.length - 1;

    if (word.type === WordType.ListItemStart) {
      block.type = TextBlockType.LIST_ITEM;
      block.pos.x = tObj.fontSizePx * 2;
      block.maxWidthPx = maxWidthPx - tObj.fontSizePx * 2;

      // change word settings for this new text logic
      word.width = 0;
    }

    wordPacketLine.words.push(word);
    wordPacketLine.width += word.width;

    const packetFitsOnLine = currentLine.width + wordPacketLine.width <= block.maxWidthPx;
    if (!isLastWord && packetFitsOnLine) {
      //Add packet without space
      for (let w = 0; w < wordPacketLine.words.length; w++) {
        if (currentLine.words.length === 0 && wordPacketLine.words[w].type === WordType.Space) {
          continue;
        }

        currentLine.words.push(wordPacketLine.words[w]);
        currentLine.width += wordPacketLine.words[w].width;
      }
      //reset packet
      wordPacketLine = getZeroTextLine();
    } else {
      //finilize line
      const lastLineWord = currentLine.words[currentLine.words.length - 1];
      if (lastLineWord) {
        if (lastLineWord.type === WordType.Space) {
          currentLine.words.length = currentLine.words.length - 1;
          currentLine.width -= lastLineWord.width;
        }
      }

      if (tObj.maxLines === 0 || lastLineNr <= tObj.maxLines) {
        block.lines.push(currentLine);
      } else {
        return [true, 0];
      }

      if (!isLastWord) {
        //New line
        currentLine = getZeroTextLine(++lastLineNr);

        //Add packet without space
        for (let w = 0; w < wordPacketLine.words.length; w++) {
          if (currentLine.words.length === 0 && wordPacketLine.words[w].type === WordType.Space) {
            continue;
          }

          currentLine.words.push(wordPacketLine.words[w]);
          currentLine.width += wordPacketLine.words[w].width;
        }
      }

      //reset packet
      wordPacketLine = getZeroTextLine();
    }
  }

  return [false, lastLineNr];
}

function layoutTextBlock(block: TextBlock, tObj: TextObject) {
  let lineYPos = 0;

  let lineWidths: number[] = block.lines.map((it) => it.width);
  block.width = Math.max(...lineWidths, block.width);
  let blockHeight = 0;

  for (let i = 0; i < block.lines.length; i++) {
    const line = block.lines[i];
    line.localPos.y = lineYPos;
    line.pos.y = block.pos.y + lineYPos;
    line.height = block.type === TextBlockType.WHITE_SPACE ? tObj.fontSizePx / 3 : tObj.fontSizePx; //no support for different font sizes in text yet

    if (tObj.textStyle.textAlign !== Alignment_Horizontal.LEFT) {
      const line = block.lines[i];
      const diff = block.width - line.width;
      if (tObj.textStyle.textAlign === Alignment_Horizontal.CENTER) {
        line.localPos.x = diff / 2;
      } else if (tObj.textStyle.textAlign === Alignment_Horizontal.RIGHT) {
        line.localPos.x = diff;
      }
    }
    line.pos.x = block.pos.x + line.localPos.x;

    layoutTextLine(line);

    blockHeight = line.localPos.y + line.height;

    //line height mods are reflected in the y-pos of the next line, not in the height
    //of the line
    lineYPos += line.height * BASE_LINE_HEIGHT_MOD * tObj.textStyle.lineHeightMod;
  }
  block.height = block.type === TextBlockType.WHITE_SPACE ? blockHeight / 30 : blockHeight;
}

function layoutTextLine(line: TextLine) {
  let wordXPos = 0;
  for (let i = 0; i < line.words.length; i++) {
    const word = line.words[i];

    word.localPos.x = wordXPos;

    word.pos.x = line.pos.x + wordXPos;
    word.pos.y = line.pos.y;

    wordXPos += word.width;
  }
}

function getZeroTextBlock(): TextBlock {
  let zeroTextBlock: TextBlock = {
    pos: v2(),
    width: 0,
    height: 0,
    words: [],
    lines: [],
    type: TextBlockType.NONE,
    maxWidthPx: 0,
    hover: false,
  };

  return zeroTextBlock;
}

//Mutates array
function removeDoubleWhiteSpaceBlocks(textBlocks: TextBlock[]) {
  for (let i = 0; i < textBlocks.length; i++) {
    const prev = i > 0 ? textBlocks[i - 1] : null;
    const cur = textBlocks[i];

    if (prev) {
      const isDoubleWhiteSpace =
        prev.type === TextBlockType.WHITE_SPACE && cur.type === TextBlockType.WHITE_SPACE;

      if (isDoubleWhiteSpace) {
        cur.type = TextBlockType.NONE;
      }
    }
  }

  for (let i = textBlocks.length - 1; i >= 0; i--) {
    if (textBlocks[i].type === TextBlockType.NONE) {
      removeDenseArrayIndexAndKeepOrder(textBlocks, i);
    }
  }
}

//Mutates array
function removeWhiteSpaceBetweenListItems(textBlocks: TextBlock[]) {
  for (let i = 0; i < textBlocks.length; i++) {
    const prev = i > 0 ? textBlocks[i - 1] : null;
    const cur = textBlocks[i];
    const next = i < textBlocks.length - 1 ? textBlocks[i + 1] : null;

    if (prev && next) {
      const isWhiteSpaceBetweenListItems =
        prev.type === TextBlockType.LIST_ITEM &&
        cur.type === TextBlockType.WHITE_SPACE &&
        next.type === TextBlockType.LIST_ITEM;

      if (isWhiteSpaceBetweenListItems) {
        cur.type = TextBlockType.NONE;
      }
    }
  }

  for (let i = textBlocks.length - 1; i >= 0; i--) {
    if (textBlocks[i].type === TextBlockType.NONE) {
      removeDenseArrayIndexAndKeepOrder(textBlocks, i);
    }
  }
}

function unpackPadding(margins: number | number[]): number[] {
  if (typeof margins === 'number') {
    return [margins, margins, margins, margins];
  } else {
    let marginsArr: number[] = margins;
    if (marginsArr.length >= 4) {
      return marginsArr;
    } else if (marginsArr.length === 3) {
      return [marginsArr[0], marginsArr[1], marginsArr[2], marginsArr[1]];
    } else if (marginsArr.length === 2) {
      return [marginsArr[0], marginsArr[1], marginsArr[0], marginsArr[1]];
    } else {
      return [marginsArr[0], marginsArr[0], marginsArr[0], marginsArr[0]];
    }
  }
}

export function getTextWidth(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  fontId: string,
  sizePx: number,
  text: string,
): number {
  ctx.fontKerning = 'normal';
  ctx.font = `${sizePx}px ${fontId}`;
  const metrics = ctx.measureText(text);
  const width = metrics.width;

  return width;
}

function getZeroTextLine(lineNr = 0): TextLine {
  let zeroTextLine: TextLine = {
    localPos: v2(),
    pos: v2(),
    width: 0,
    height: 0,
    words: [],
    lineNr,
    hover: false,
  };

  return zeroTextLine;
}
